import { WEEK_ORDER, type BookingFor, type SetupStepKey } from "@teamna/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { createContext, useContext, type ReactNode } from "react";
import {
  Button,
  EmptyState,
  ErrorState,
  LockIcon,
  Notice,
  PageHeader,
  SkeletonList,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useSetupProgress } from "@teamna/web-client/hooks/use-setup.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { gateCopy, setupPath, setupSteps, stepCopy, stepNumber } from "./steps.ts";

/**
 * The frame of every setup page: the step number, the title and line in the business's words,
 * "Back to Home", and the gate.
 *
 * A step the API marks as not available (invite before sessions and packages, placement before
 * any participant, balances before packages) shows why and links to the steps it waits on, the
 * same answer the mobile menu gives when the step is tapped early. The page body is not rendered
 * behind a gate, so nobody fills in a form the API would refuse.
 *
 * Wider than the 760px reading frame of the older owner pages: these pages hold tables.
 */
export function SetupFrame({
  step,
  actions,
  children,
}: {
  step: SetupStepKey;
  /** Buttons on the right of the title. */
  actions?: ReactNode;
  /** Receives the business id once the business is known. */
  children: (businessId: string) => ReactNode;
}) {
  const { business, isLoading, error } = useActiveBusiness();
  const navigate = useNavigate();
  const { label } = useLabels();
  const { bookingFor } = useShape().shape;
  const businessId = business?.businessId ?? null;
  const progress = useSetupProgress(businessId);
  const order = setupSteps(bookingFor);
  const copy = stepCopy(step, label, bookingFor);
  const state = progress.data?.steps.find((row) => row.key === step);

  let body: ReactNode;
  if (isLoading && !business) {
    body = <SkeletonList />;
  } else if (error && !business) {
    body = <ErrorState error={error} />;
  } else if (!businessId) {
    body = (
      <EmptyState
        icon={<LockIcon size={30} />}
        title="No business to manage"
        message="This account does not run a business yet. Create one from the start page."
      />
    );
  } else if (state && !state.available) {
    const waitsOn = order.filter(
      (key) =>
        (step === "invite_sent" && (key === "sessions" || key === "packages")) ||
        (step === "balances" && key === "packages"),
    );
    body = (
      <div className="flex flex-col gap-4">
        <Notice tone="info" icon={<LockIcon size={17} />} title="This step is not open yet">
          {gateCopy(step, label, bookingFor)}
        </Notice>
        {waitsOn.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {waitsOn.map((key) => (
              <Link
                key={key}
                to={setupPath(key)}
                className="inline-flex min-h-11 items-center rounded-xl border border-line bg-paper px-4 text-[14px] font-semibold text-accent-strong"
              >
                Step {stepNumber(key, bookingFor)}: {stepCopy(key, label, bookingFor).title}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    );
  } else {
    body = children(businessId);
  }

  return (
    <div>
      <PageHeader
        back={{ to: "/owner/home", label: "Back to Home" }}
        eyebrow={`Setup step ${stepNumber(step, bookingFor)} of ${order.length}${state?.done ? ", done" : ""}`}
        title={copy.title}
        description={copy.description}
        {...(actions && businessId && state?.available !== false ? { actions } : {})}
      />
      {body}
      {businessId ? (
        <SetupFooter
          done={Boolean(state?.done)}
          next={progress.data?.nextStep && progress.data.nextStep !== step ? progress.data.nextStep : null}
          complete={Boolean(progress.data && progress.data.done >= progress.data.total)}
          label={label}
          bookingFor={bookingFor}
          onNext={(key) => void navigate({ to: setupPath(key) })}
        />
      ) : null}
    </div>
  );
}

/**
 * Where "Book a call with us" and "ask a question" go. Desk provides it; Control Center (Teamna staff
 * acting as the business) does not, so the links do not show there.
 *
 * - `callUrl`: Anas's booking page (Google Calendar appointments), opened in a new tab. Google tells
 *   him of each booking, so no Teamna request is made (Anas, 2026-10-14).
 * - `requestPath`: a Help and support request, for a question.
 * - `onCallOpened`: Desk sends `call_booking_opened` with it.
 */
export type SetupHelp = {
  requestPath: string;
  callUrl: string;
  onCallOpened?: (source: "setup_page" | "home") => void;
};
const SetupHelpContext = createContext<SetupHelp | null>(null);
/** The help paths, or null in Control Center. */
export function useSetupHelp(): SetupHelp | null {
  return useContext(SetupHelpContext);
}

export function SetupHelpProvider({ value, children }: { value: SetupHelp; children: ReactNode }) {
  return <SetupHelpContext.Provider value={value}>{children}</SetupHelpContext.Provider>;
}

/**
 * Under every setup page (decision D7, 2026-10-14): once the step is done, the way to the next open
 * step, so setup is a path and not only a hub. And on every step, the way to a person: a call or a
 * question, both through Help and support.
 */
function SetupFooter({
  done,
  next,
  complete,
  label,
  bookingFor,
  onNext,
}: {
  done: boolean;
  next: SetupStepKey | null;
  complete: boolean;
  label: Parameters<typeof stepCopy>[1];
  bookingFor: BookingFor;
  onNext: (key: SetupStepKey) => void;
}) {
  const help = useContext(SetupHelpContext);
  const requestPath: string | null = help?.requestPath ?? null;
  return (
    <div className="mt-10 flex flex-col gap-4 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
      {done && next ? (
        // Opening the next page writes nothing, so it stays on when the plan is read-only (review F3.1).
        <Button readOnlySafe onClick={() => onNext(next)}>
          Next: step {stepNumber(next, bookingFor)}, {stepCopy(next, label, bookingFor).title}
        </Button>
      ) : done && complete ? (
        <p className="text-[14px] font-semibold text-confirmed">Setup is complete.</p>
      ) : (
        <span />
      )}
      {help && requestPath ? (
        <p className="text-[14px] text-ink-2">
          Need help?{" "}
          <a
            href={help.callUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => help.onCallOpened?.("setup_page")}
            className="font-semibold text-accent-strong"
          >
            Book a call with us
          </a>{" "}
          or{" "}
          <Link to={requestPath} className="font-semibold text-accent-strong">
            ask a question
          </Link>
          .
        </p>
      ) : null}
    </div>
  );
}

/** The save bar under a setup form: the primary action on the left at desktop, full width on a phone. */
export function SaveBar({ children, note }: { children: ReactNode; note?: string }) {
  return (
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex flex-col gap-2 sm:flex-row">{children}</div>
      {note ? <p className="text-[13px] text-ink-3">{note}</p> : null}
    </div>
  );
}

const NO_DAYS: number[] = [];
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function weekdayShort(day: number): string {
  return WEEKDAY_SHORT[day] ?? "";
}

/** "Sat, Mon, Wed", in week order. */
export function describeWeekdays(days: number[]): string {
  if (days.length === 0) return "No usual days";
  return WEEK_ORDER.filter((day) => days.includes(day))
    .map(weekdayShort)
    .join(", ");
}

/**
 * Seven day toggles. Real toggle buttons (`aria-pressed`), so each one is reachable by keyboard
 * and announced with its state. Days off can be shown disabled.
 */
export function WeekdayToggles({
  selected,
  onChange,
  disabledDays = NO_DAYS,
  label,
  compact = false,
}: {
  selected: number[];
  onChange: (next: number[]) => void;
  disabledDays?: number[];
  /** Accessible name of the group, for example "Days for Level 1". */
  label: string;
  compact?: boolean;
}) {
  return (
    <fieldset className="relative min-w-0 border-0 p-0">
      <legend className="sr-only">{label}</legend>
      <div className={compact ? "flex flex-nowrap gap-1" : "flex flex-wrap gap-1.5"}>
        {WEEK_ORDER.map((day) => {
          const on = selected.includes(day);
          const off = disabledDays.includes(day);
          return (
            <button
              key={day}
              type="button"
              aria-pressed={on}
              disabled={off}
              title={off ? "Day off" : undefined}
              onClick={() =>
                onChange(on ? selected.filter((d) => d !== day) : [...selected, day].sort((a, b) => a - b))
              }
              className={[
                "rounded-lg border font-semibold transition-colors duration-150 disabled:opacity-35",
                compact ? "h-9 min-w-8 px-0.5 text-[12px]" : "h-10 min-w-12 px-2 text-[13px]",
                on
                  ? "sk-tile [--sk-p:var(--color-accent)] border-transparent text-inverse"
                  : "sk-chip text-ink-2 hover:brightness-[1.02]",
              ].join(" ")}
            >
              {weekdayShort(day)}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/** A number with minus and plus buttons, for places, minutes and sessions left. */
export function Stepper({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  label,
}: {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** "Sessions for Omar", read with the buttons: "Fewer sessions for Omar". */
  label: string;
}) {
  const button =
    "flex size-9 items-center justify-center rounded-lg border border-line bg-paper text-[18px] font-semibold text-ink transition-colors duration-150 hover:bg-surface disabled:opacity-35";
  return (
    <fieldset className="relative inline-flex items-center gap-1.5 border-0 p-0">
      <legend className="sr-only">{label}</legend>
      <button
        type="button"
        className={button}
        aria-label={`Less: ${label}`}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - step))}
      >
        −
      </button>
      <output
        aria-live="polite"
        className="min-w-9 text-center text-[17px] font-semibold tabular-nums"
      >
        {value}
      </output>
      <button
        type="button"
        className={button}
        aria-label={`More: ${label}`}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + step))}
      >
        +
      </button>
    </fieldset>
  );
}
